import React, {useState, useRef, MouseEvent} from 'react';
import cx from 'classnames';
import {priority} from '@priority-software/priority-proto-api/lib';
import {
    Button,
    HTMLDialog,
    Flex,
    Text,
    Input,
    Icon,
    PriorityIcon
} from '@priority-software/priority-style-react';
import styles from './styles.module.scss';
import {texts} from '../../texts';
import {DialogToolbarContent} from '../../../../components/Dialog/DialogToolbar/DialogToolbar';
import {FormDatePicker} from '../../../../features/Form/components/Input/DateInput/DatePicker';
import {formatDate} from '../../../../features/Form/utils/date';
import {Column} from '../../../../stores/Form/Column';
import {hasNodeIgnoreClassName} from '../../../../utils/clickOutsideDefaultHandler';
import {DataTestIds} from '../../constants';

type Props = {
    closeDialog: () => void;
    deliveryDateCol: Column;
    onAssign: (date: string) => void;
    zIndex: number;
};

const dialogId = 'autoPlanningDialog';
const igonoreClassName = 'priority-datepicker';
export const AutoPlanningDlg = ({closeDialog, deliveryDateCol, onAssign, zIndex}: Props) => {
    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
    const [value, setValue] = useState<string>(
        formatDate(new Date(), priority.netitems.form.ColumnType.Date8)
    );
    const inputRef = useRef<HTMLInputElement>();

    const onClickOutside = (e: MouseEvent<HTMLElement>) => {
        const ignore = hasNodeIgnoreClassName(e.target as HTMLElement, igonoreClassName);
        if (ignore) {
            return;
        }
        setIsDatepickerOpen(false);
    };

    const clearDateValue = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onDateChange('');
        setIsDatepickerOpen(false);
    };

    const onDateChange = (date: string) => {
        setValue(date);
    };

    const onConfirm = () => {
        onAssign(value);
    };

    return (
        <HTMLDialog
            id={dialogId}
            isOpen={true}
            onClose={closeDialog}
            style={{visibility: zIndex >= 0 ? 'visible' : 'hidden'}}
            toolbarContent={<DialogToolbarContent mainTitle={texts.auto_planning} />}
            content={
                <Flex direction="column" alignItems="flex-start" componentClasses={styles.content}>
                    <Text skin="paragraph-1" dataTestId={DataTestIds.dialogInstructions}>
                        {texts.auto_planning_instruction_text}
                    </Text>
                    <div
                        className={cx(styles.datePickerInputWrapper, igonoreClassName)}
                        onClick={() => setIsDatepickerOpen(true)}
                    >
                        <Input
                            value={value}
                            label={texts.delivery_date}
                            readOnly
                            prefixIcon={
                                <div className={styles.datePickerTriggerWrapper}>
                                    <FormDatePicker
                                        value={value}
                                        columnType={deliveryDateCol.Type}
                                        inputRef={inputRef}
                                        isOpen={isDatepickerOpen}
                                        onChange={selected => onDateChange(selected)}
                                        setIsOpen={setIsDatepickerOpen}
                                        portalEl={document.getElementById(dialogId)}
                                        onClickOutside={onClickOutside}
                                        position="fixed"
                                        dataTestId={DataTestIds.datePicker}
                                    />
                                </div>
                            }
                            dataTestId={DataTestIds.deliveryDateInput}
                        >
                            <Icon
                                icon={PriorityIcon.closeSmall}
                                onClick={e => clearDateValue(e)}
                                dataTestId={DataTestIds.deliveryDateClearBtn}
                            />
                        </Input>
                    </div>
                    <div ref={inputRef} className={styles.datePickerPosition}></div>
                </Flex>
            }
            footer={
                <Flex justifyContent="flex-end" componentClasses={styles.dialogFooter}>
                    <Button skin="secondary" size="medium" wideButton onClick={closeDialog}>
                        {texts.cancel_btn}
                    </Button>
                    <Button
                        skin="primary"
                        size="medium"
                        wideButton
                        onClick={onConfirm}
                        dataTestId={DataTestIds.confirmBtn}
                    >
                        {texts.add_to_route_btn}
                    </Button>
                </Flex>
            }
        />
    );
};
